import { addToolBreadcrumb } from '@tunasong/models'
import { ChordLib } from '@tunasong/music-lib'
import type { Chord, Substitution } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { Button, useSearchParams } from '@tunasong/ui-lib'
import { useEffect, useState, type FC } from 'react'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { ReharmAbToggle, type AbMode } from './reharm-ab-toggle.js'
import { parseKeyParam } from './reharm-lab-params.js'
import { useReharmLabPlayback } from './reharm-lab-playback.js'
import { ReharmProgressionStrip, type ActiveSubstitution } from './reharm-progression-strip.js'
import { ReharmSidePanel } from './reharm-side-panel.js'
import styles from './reharm-lab.module.css'

const DEFAULT_PROGRESSION: Chord[] = [
  { root: 'C', variant: '' },
  { root: 'A', variant: 'm' },
  { root: 'F', variant: '' },
  { root: 'G', variant: '' },
]

const applySubstitution = (progression: Chord[], active: ActiveSubstitution | null): Chord[] => {
  if (!active) {return progression}
  const { slotIndex, substitution } = active
  switch (substitution.kind) {
    case 'replace':
      return progression.map((c, i) => (i === slotIndex ? substitution.chord : c))
    case 'insert-before':
      return [...progression.slice(0, slotIndex), substitution.chord, ...progression.slice(slotIndex)]
    case 'replace-next':
      return progression.map((c, i) => (i === slotIndex + 1 ? substitution.chord : c))
  }
}

/**
 * Reharm Lab v1 — the first creative tool in the Music-Theory Learning
 * suite. A 4-slot progression strip with a side panel offering the five
 * Bowie-locked substitution techniques; A/B toggle compares Original vs
 * Reharm. Lives at `/app/reharm`.
 *
 * Spec: docs/superpowers/specs/2026-05-19-reharm-lab-design.md
 */
export const ReharmLab: FC = () => {
  const [searchParams] = useSearchParams()
  const [key] = useState<Scale>(() => parseKeyParam(searchParams.get('key')))
  const [progression] = useState<Chord[]>(DEFAULT_PROGRESSION)
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null)
  const [activeSubstitution, setActiveSubstitution] = useState<ActiveSubstitution | null>(null)
  const [abMode, setAbMode] = useState<AbMode>('original')

  // Polish 6/6 — tool telemetry. Open-event breadcrumb on mount.
  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'reharm-lab' })
  }, [])

  const effectiveProgression = abMode === 'reharm' ? applySubstitution(progression, activeSubstitution) : progression
  const { play } = useReharmLabPlayback({ chords: effectiveProgression })

  const handleApply = (slotIndex: number, substitution: Substitution): void => {
    if (!substitution.available) {return}
    setActiveSubstitution({ slotIndex, substitution })
    setAbMode('reharm')
  }

  const handleReset = (): void => {
    setActiveSubstitution(null)
    setAbMode('original')
  }

  // §8.5 chord round-trip — pass the effective progression (post-substitution
  // when A/B is on reharm) to Cadence Lab so the user lands with the chord
  // context they've been working with.
  const cadenceChordsParam = encodeURIComponent(effectiveProgression.map(c => ChordLib.getName(c)).join(','))
  const relatedTools: RelatedTool[] = [
    { targetRoute: `/app/modes?tonic=${key.root}`, lexiconId: 'crosslink-reharm-to-mode' },
    { targetRoute: `/app/quiz?key=${key.root}&keyType=major`, lexiconId: 'crosslink-reharm-to-quiz' },
    {
      targetRoute: `/app/cadences?scale=${key.root}-major&chords=${cadenceChordsParam}`,
      lexiconId: 'crosslink-reharm-to-cadence',
    },
  ]

  return (
    <div className={styles.lab}>
      <section className={styles.workspace}>
        <div className={styles.left}>
          <ReharmProgressionStrip
            progression={progression}
            keyScale={key}
            activeSubstitution={activeSubstitution}
            selectedSlotIndex={selectedSlotIndex}
            onSlotClick={setSelectedSlotIndex}
          />
          <div className={styles.controls}>
            <ReharmAbToggle
              hasReharm={activeSubstitution !== null}
              abMode={abMode}
              onModeChange={setAbMode}
              onPlay={play}
            />
            {activeSubstitution !== null ? (
              <Button variant="text" onClick={handleReset}>
                Reset to Original
              </Button>
            ) : null}
          </div>
        </div>

        <ReharmSidePanel
          selectedSlotIndex={selectedSlotIndex}
          progression={progression}
          keyScale={key}
          activeSubstitution={activeSubstitution}
          onApply={handleApply}
        />
      </section>

      <RelatedTools tools={relatedTools} />
    </div>
  )
}
